// src/auth/AuthProvider.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Standalone auth for v0.8.0. Flow:
//   1. User signs in with Firebase (Google, PULSE tenant) → Firebase ID token.
//   2. POST that token to /api/pulse/auth/exchange → short-lived S2S token.
//   3. Hold the S2S token in memory (api.setServiceToken), never localStorage.
//   4. The exchange result carries isAdmin, used for first-render role gating.
//
// Dev bypass: when VITE_DEV_FAKE_AUTH=1 the app skips Firebase and exchanges a
// raw email directly (the local API must be running in a dev posture). This is
// never enabled in deployed envs.
// ─────────────────────────────────────────────────────────────────────────────

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { exchangeFirebaseToken, setServiceToken } from "@/lib/api";
import { DEV_FAKE_AUTH } from "@/lib/env";

export type AuthStatus = "anonymous" | "authenticating" | "authenticated" | "error";

export interface AuthState {
  status: AuthStatus;
  email: string | null;
  isAdmin: boolean;
  error: string | null;
}

export interface AuthContextValue extends AuthState {
  signIn: () => Promise<void>;
  /** Dev-only: exchange a raw email (requires VITE_DEV_FAKE_AUTH=1). */
  devSignIn: (email: string) => Promise<void>;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const INITIAL: AuthState = {
  status: "anonymous",
  email: null,
  isAdmin: false,
  error: null,
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(INITIAL);

  const completeExchange = useCallback(
    async (exchange: () => Promise<{ token: string; email: string; isAdmin: boolean }>) => {
      setState((s) => ({ ...s, status: "authenticating", error: null }));
      try {
        const result = await exchange();
        setServiceToken(result.token);
        setState({
          status: "authenticated",
          email: result.email,
          isAdmin: result.isAdmin,
          error: null,
        });
      } catch (err) {
        setServiceToken(null);
        const message = err instanceof Error ? err.message : "sign-in failed";
        setState({ status: "error", email: null, isAdmin: false, error: message });
      }
    },
    []
  );

  const signIn = useCallback(async () => {
    await completeExchange(async () => {
      // Imported lazily so the Firebase SDK never loads in dev-fake mode.
      const { signInWithGoogle } = await import("@/lib/firebase");
      const idToken = await signInWithGoogle();
      return exchangeFirebaseToken(idToken);
    });
  }, [completeExchange]);

  const devSignIn = useCallback(
    async (email: string) => {
      if (!DEV_FAKE_AUTH) {
        setState({
          status: "error",
          email: null,
          isAdmin: false,
          error: "dev sign-in is disabled (VITE_DEV_FAKE_AUTH is not set)",
        });
        return;
      }
      // The dev exchange endpoint accepts a raw email in place of a Firebase
      // token when the server runs in a dev posture.
      await completeExchange(() => exchangeFirebaseToken(`dev:${email}`));
    },
    [completeExchange]
  );

  const signOut = useCallback(async () => {
    setServiceToken(null);
    setState(INITIAL);
    if (!DEV_FAKE_AUTH) {
      const { firebaseSignOut } = await import("@/lib/firebase");
      await firebaseSignOut().catch(() => undefined);
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, signIn, devSignIn, signOut }),
    [state, signIn, devSignIn, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
